import { useEffect, useMemo, useState } from "react";
import { apiGetSettings, apiCreateSetting, apiUpdateSetting } from "../../graphQL/settings";
import { useSettingsDomain, getSettingsDomain, settingsScope } from "../../graphQL/settings/scope";

// Single source of truth for contact categories (settings doc:
// type "addressbook", schemaType "category"). Every category menu reads
// through this module; adding a category anywhere (contact settings,
// bulk upload) refreshes every mounted consumer via the shared store.

const cache = new Map(); // domain key -> normalized categories array
const listeners = new Set();

const notify = (key) => listeners.forEach((fn) => fn(key));

const pickLatest = (settings) => {
  const list = Array.isArray(settings) ? settings : [];
  return list.reduce((acc, cur) => {
    const accTs = Number(acc?.updatedAt ?? acc?.createdAt ?? 0);
    const curTs = Number(cur?.updatedAt ?? cur?.createdAt ?? 0);
    return curTs >= accTs ? cur : acc;
  }, list[0] || null);
};

const extractDataArray = (setting) => {
  const payload = setting?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeCategories = (raw) =>
  raw.map((cat, i) => ({
    ...cat,
    id: cat?.id || cat?.value || `cat_${i}`,
    isActive:
      typeof cat?.isActive === "boolean"
        ? cat.isActive
        : (typeof cat?.style?.isActive === "boolean" ? cat.style.isActive : true),
  }));

const fetchLatestSetting = async (key) => {
  const input = {
    schemaType: "category",
    type: "addressbook",
    ...settingsScope(key),
  };
  const result = await apiGetSettings(input);
  return pickLatest(result?.settings);
};

const loadCategories = async (key) => {
  const latest = await fetchLatestSetting(key);
  const categories = normalizeCategories(extractDataArray(latest));
  cache.set(key, categories);
  notify(key);
  return categories;
};

// Re-derive options from the settings doc. With no argument, refreshes every
// domain key any mounted menu has loaded (call after saving categories).
export const refreshContactCategories = async (domain) => {
  const keys = domain != null ? [String(domain || "")] : Array.from(cache.keys());
  await Promise.all(keys.map((key) => loadCategories(key)));
};

export const categoryValueFromName = (name) =>
  String(name || "").trim().toLowerCase().replace(/\s+/g, "_");

// Persist a new category on the shared settings doc (no-op if it already
// exists), then refresh every mounted menu. Returns the canonical value.
export const addContactCategory = async ({ name }) => {
  const label = String(name || "").trim();
  const value = categoryValueFromName(label);
  if (!value) return "";
  const key = getSettingsDomain();
  const latest = await fetchLatestSetting(key);
  const data = extractDataArray(latest);
  const exists = data.some((cat) =>
    [cat?.value, cat?.id, cat?.name, cat?.label]
      .map((v) => String(v || "").trim().toLowerCase())
      .includes(value)
  );
  if (!exists) {
    const next = [
      ...data,
      {
        id: `cat_${Date.now().toString(36)}`,
        name: label,
        value,
        bgColor: "#E3F2FD",
        textColor: "#0D47A1",
        isActive: true,
      },
    ];
    const isSynthetic = typeof latest?.id === "string" && latest.id.startsWith("default_");
    const input = {
      schemaType: "category",
      type: "addressbook",
      ...settingsScope(key),
      data: next,
    };
    if (latest?.id && !isSynthetic) {
      await apiUpdateSetting({ id: latest.id, ...input });
    } else {
      await apiCreateSetting(input);
    }
  }
  await refreshContactCategories();
  return value;
};

// Scope comes from the active domain; the legacy authorId argument is ignored
// (kept so existing call sites keep working).
export const useContactCategories = () => {
  const key = useSettingsDomain();
  const [categories, setCategories] = useState(() => cache.get(key) || []);

  useEffect(() => {
    const onChange = (changedKey) => {
      if (changedKey === key) setCategories(cache.get(key) || []);
    };
    listeners.add(onChange);
    if (cache.has(key)) {
      setCategories(cache.get(key) || []);
    } else {
      loadCategories(key).catch((e) => console.error("Failed to load contact categories:", e));
    }
    return () => {
      listeners.delete(onChange);
    };
  }, [key]);

  return categories;
};

// Active category values (deduped, sorted) for plain string menus.
export const useContactCategoryOptions = () => {
  const categories = useContactCategories();
  return useMemo(() => {
    const values = categories
      .filter((cat) => cat && (cat.isActive ?? true))
      .map((cat) => String(cat.value || cat.name || cat.label || "").trim())
      .filter(Boolean);
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [categories]);
};
